import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Animated, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { fontSize, radius, shadow, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/contexts/ProfileContext';
import { supabase } from '@/lib/supabase';
import {
  fetchOpenRequests,
  countNewOpenRequests,
  getRequestCountsByRegion,
  listUsedRequestCategories,
  type ServiceRequest,
} from '@/lib/serviceRequests';
import { fetchMySecondaryCategories } from '@/lib/secondaryCategories';
import { GreeceMap } from '@/components/GreeceMap';
import { Avatar } from '@/components/ui/Avatar';
import { fetchMyReliabilityPercent } from '@/lib/reviews';
import { reliabilityColorFor } from '@/lib/format';

/** Οθόνη αποκλειστικά για επαγγελματίες: περιήγηση στον δημόσιο πίνακα
 * "Ψάχνουν για προσφορά" (ανοιχτά αιτήματα πελατών), με χάρτη+φίλτρα ανά
 * περιοχή/κατηγορία. Ξεχωριστή από το "Βρες επαγγελματίες" — το "Αιτήματα
 * Προσφοράς" εκεί αφορά τα ΔΙΚΑ ΣΟΥ αιτήματα, όχι αυτόν τον πίνακα. */
export default function FindClientsScreen() {
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const router = useRouter();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<number | null>(null);
  const [usedCategories, setUsedCategories] = useState<{ id: number; name: string }[]>([]);
  const [allCategories, setAllCategories] = useState<{ id: number; name: string }[]>([]);
  const [requestRegionCounts, setRequestRegionCounts] = useState<Record<string, number>>({});
  const [showMap, setShowMap] = useState(true);
  const showResultsBounce = useRef(new Animated.Value(0)).current;

  // Bounce στο κουμπί "Εμφάνιση" ανά 3 δευτερόλεπτα, όσο ο χάρτης είναι ανοιχτός.
  useEffect(() => {
    if (!showMap) return;
    const loop = Animated.loop(
      Animated.sequence([
        Animated.delay(3000),
        Animated.timing(showResultsBounce, { toValue: 1, duration: 200, useNativeDriver: Platform.OS !== 'web' }),
        Animated.timing(showResultsBounce, { toValue: 0, duration: 200, useNativeDriver: Platform.OS !== 'web' }),
      ]),
    );
    loop.start();
    return () => { loop.stop(); showResultsBounce.setValue(0); };
  }, [showMap, showResultsBounce]);

  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [newRequestsCount, setNewRequestsCount] = useState(0);
  const [requestsSeenBefore, setRequestsSeenBefore] = useState<string | null>(null);
  const [requesterInfo, setRequesterInfo] = useState<Record<string, { avatar_url: string | null; full_name: string | null; reliability: number | null }>>({});
  // Ενημερωτικό μήνυμα ζήτησης στον κλάδο του επαγγελματία — 'region' αν
  // υπάρχει ανοιχτό αίτημα στη δική του περιοχή, 'remote' αν υπάρχει μόνο
  // αίτημα με περιοχή "Όλες" (εξ αποστάσεως) στην κατηγορία του.
  const [demandNotice, setDemandNotice] = useState<'region' | 'remote' | null>(null);

  useEffect(() => {
    if (!user || !profile?.profession_category_id) { setDemandNotice(null); return; }
    void (async () => {
      try {
        const secondary = await fetchMySecondaryCategories(user.id).catch(() => []);
        const categoryIds = [profile.profession_category_id!, ...secondary.map((c) => c.id)];
        const list = await fetchOpenRequests(categoryIds, user.id, null);
        const hasRegionDemand = !!profile.region && list.some((r) => r.region === profile.region);
        const hasRemoteDemand = list.some((r) => r.region == null);
        setDemandNotice(hasRegionDemand ? 'region' : hasRemoteDemand ? 'remote' : null);
      } catch {
        setDemandNotice(null);
      }
    })();
  }, [user, profile?.profession_category_id, profile?.region]);

  useEffect(() => {
    void listUsedRequestCategories().then(setUsedCategories).catch(() => setUsedCategories([]));
    void supabase.from('profession_categories').select('id, name').order('name')
      .then(({ data }) => { if (data) setAllCategories(data as { id: number; name: string }[]); });
  }, []);

  useEffect(() => {
    void getRequestCountsByRegion(categoryFilter).then(setRequestRegionCounts).catch(() => setRequestRegionCounts({}));
  }, [categoryFilter]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      // Δεν έχει νόημα να βλέπει ο επαγγελματίας τα δικά του αιτήματα εδώ (μπερδεύει) —
      // αυτά τα διαχειρίζεται από τα "Αιτήματα Προσφοράς" του, όχι από αυτόν τον πίνακα.
      const list = await fetchOpenRequests(categoryFilter, user?.id ?? null, regionFilter);
      setRequests(list);

      const userIds = [...new Set(list.map((r) => r.user_id))];
      if (userIds.length > 0) {
        const { data: profiles } = await supabase.rpc('get_profiles_public', { p_ids: userIds });
        const rows = (profiles ?? []) as { id: string; full_name: string | null; avatar_url: string | null; phone_verified: boolean | null; verified: boolean | null }[];
        const info: Record<string, { avatar_url: string | null; full_name: string | null; reliability: number | null }> = {};
        await Promise.all(rows.map(async (p) => {
          const reliability = await fetchMyReliabilityPercent(p.id, p.phone_verified, p.verified).catch(() => null);
          info[p.id] = { avatar_url: p.avatar_url, full_name: p.full_name, reliability };
        }));
        setRequesterInfo(info);
      } else {
        setRequesterInfo({});
      }
    } catch {
      setRequests([]);
    } finally {
      setRequestsLoading(false);
    }
  }, [user, categoryFilter, regionFilter]);

  useFocusEffect(useCallback(() => {
    void loadRequests();
    void (async () => {
      try {
        const lastSeen = await AsyncStorage.getItem('requests_last_seen');
        setRequestsSeenBefore(lastSeen);
        setNewRequestsCount(await countNewOpenRequests(lastSeen, user?.id));
        await AsyncStorage.setItem('requests_last_seen', new Date().toISOString());
      } catch { setNewRequestsCount(0); }
    })();
  }, [loadRequests, user?.id]));

  function selectRegion(region: string | null) {
    setRegionFilter(region);
  }

  const requestCategoryName = (id: number | null) => allCategories.find((c) => c.id === id)?.name ?? null;

  return (
    <View style={[styles.root, { paddingTop: insets.top }]}>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={() => router.back()} hitSlop={10} accessibilityRole="button">
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </Pressable>
        <View style={styles.headerTitle}>
          <Ionicons name="people-outline" size={20} color={colors.professional} />
          <Text style={[styles.headerText, { color: colors.professional }]}>{t('home.findClients')}</Text>
        </View>
        <View style={styles.spacer} />
      </View>

      <Text style={styles.requestsSubtitle}>{t('requests.subtitleForProfessionals')}</Text>

      {!!demandNotice && (
        <View style={styles.demandNotice}>
          <Ionicons name="flame-outline" size={16} color={colors.primary} />
          <Text style={styles.demandNoticeText}>
            {demandNotice === 'region' ? t('requests.demandInRegion') : t('requests.demandRemote')}
          </Text>
        </View>
      )}

      {showMap && (
        <View style={styles.mapSection}>
          <GreeceMap
            countsByRegion={{}}
            requestCountsByRegion={requestRegionCounts}
            selectedRegion={regionFilter}
            onSelectRegion={selectRegion}
          />
          {regionFilter && (
            <Pressable style={styles.mapSelectedChip} onPress={() => selectRegion(null)} accessibilityRole="button">
              <Ionicons name="location" size={13} color={colors.primary} />
              <Text style={styles.mapSelectedChipText}>{regionFilter}</Text>
              <Ionicons name="close-circle" size={15} color={colors.textMuted} />
            </Pressable>
          )}

          {usedCategories.length > 0 && (
            <View style={styles.inlineCategorySection}>
              <Text style={styles.filterSectionLabel}>{t('circle.filter_profession')}</Text>
              <View style={styles.filterChipsWrap}>
                <Pressable
                  style={[styles.filterChip, categoryFilter === null && styles.filterChipActive]}
                  onPress={() => setCategoryFilter(null)}
                >
                  <Text style={[styles.filterChipText, categoryFilter === null && styles.filterChipTextActive]}>{t('circle.filter_professionAll')}</Text>
                </Pressable>
                {usedCategories.map((cat) => (
                  <Pressable
                    key={cat.id}
                    style={[styles.filterChip, categoryFilter === cat.id && styles.filterChipActive]}
                    onPress={() => setCategoryFilter(categoryFilter === cat.id ? null : cat.id)}
                  >
                    <Text style={[styles.filterChipText, categoryFilter === cat.id && styles.filterChipTextActive]}>{cat.name}</Text>
                  </Pressable>
                ))}
              </View>
            </View>
          )}
        </View>
      )}

      {requestsLoading ? (
        <View style={styles.feedEmpty}>
          <ActivityIndicator color={colors.primary} />
        </View>
      ) : requests.length === 0 ? (
        <View style={styles.feedEmpty}>
          <Ionicons name="megaphone-outline" size={40} color={colors.border} />
          <Text style={styles.feedEmptyText}>{t('requests.emptyOpen')}</Text>
        </View>
      ) : (
        <ScrollView contentContainerStyle={[styles.requestsList, { paddingBottom: insets.bottom + spacing.xl }]}>
          {requests.map((r) => (
            <Pressable
              key={r.id}
              style={styles.requestCard}
              onPress={() => router.push({ pathname: '/(app)/requests/[id]', params: { id: r.id } } as any)}
              accessibilityRole="button"
            >
              {r.status === 'open' && (!requestsSeenBefore || r.created_at > requestsSeenBefore) && (
                <View style={styles.requestNewDot} />
              )}
              {(() => {
                const info = requesterInfo[r.user_id];
                if (!info) return null;
                const relColor = reliabilityColorFor(info.reliability, colors.success, colors.textMuted);
                return (
                  <View style={styles.requesterRow}>
                    <Avatar uri={info.avatar_url} name={info.full_name} size={22} />
                    <Text style={styles.requesterName} numberOfLines={1}>{info.full_name?.trim() || t('common.user')}</Text>
                    {info.reliability != null && (
                      <Text style={[styles.requesterReliability, { color: relColor }]}>{info.reliability.toFixed(0)}%</Text>
                    )}
                  </View>
                );
              })()}
              <View style={styles.requestCardTop}>
                <Text style={styles.requestCardTitle} numberOfLines={1}>{r.title}</Text>
                {r.status !== 'open' && (
                  <View style={styles.statusBadge}>
                    <Text style={styles.statusBadgeText}>
                      {r.status === 'closed' ? t('requests.status_closed') : t('requests.status_cancelled')}
                    </Text>
                  </View>
                )}
              </View>
              {!!r.description && <Text style={styles.requestCardDesc} numberOfLines={2}>{r.description}</Text>}
              <View style={styles.cardChipsRow}>
                {!!requestCategoryName(r.profession_category_id) && (
                  <View style={styles.categoryChip}>
                    <Text style={styles.categoryChipText}>{requestCategoryName(r.profession_category_id)}</Text>
                  </View>
                )}
                {r.user_id === user?.id && (
                  <View style={styles.ownChip}>
                    <Text style={styles.ownChipText}>{t('requests.ownTag')}</Text>
                  </View>
                )}
                {!r.region && !!regionFilter && (
                  <View style={styles.remoteChip}>
                    <Text style={styles.remoteChipText}>{t('requests.remoteTag')}</Text>
                  </View>
                )}
              </View>
            </Pressable>
          ))}
        </ScrollView>
      )}

      {showMap ? (
        <Animated.View
          style={{
            position: 'absolute', right: spacing.lg, bottom: spacing.lg,
            transform: [{ scale: showResultsBounce.interpolate({ inputRange: [0, 1], outputRange: [1, 1.15] }) }],
          }}
        >
          <Pressable style={styles.showResultsPillInner} onPress={() => setShowMap(false)} accessibilityRole="button">
            <Ionicons name="checkmark-circle" size={18} color="#fff" />
            <Text style={styles.showResultsPillText}>{t('circle.showResults')}</Text>
          </Pressable>
        </Animated.View>
      ) : (
      <Pressable
        style={[styles.fab, (regionFilter !== null || categoryFilter !== null) && styles.fabActive]}
        onPress={() => setShowMap((v) => !v)}
        accessibilityRole="button"
        accessibilityLabel={t('circle.filters')}
      >
        <Ionicons
          name="map-outline"
          size={22}
          color={(regionFilter !== null || categoryFilter !== null) ? '#fff' : colors.primary}
        />
        {(regionFilter !== null || categoryFilter !== null) && <View style={styles.fabDot} />}
      </Pressable>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    root: { flex: 1, backgroundColor: colors.background },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: spacing.lg,
      paddingVertical: spacing.md,
    },
    headerTitle: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs },
    spacer: { width: 24 },
    headerText: { color: colors.text, fontSize: fontSize.lg, fontWeight: '700' },
    mapSection: {
      paddingHorizontal: spacing.md,
      paddingBottom: spacing.sm,
      alignItems: 'center',
      gap: spacing.xs,
    },
    mapSelectedChip: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      backgroundColor: `${colors.primary}18`,
      borderRadius: radius.full,
      paddingHorizontal: spacing.sm,
      paddingVertical: 5,
    },
    mapSelectedChipText: { color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    inlineCategorySection: {
      width: '100%',
      marginTop: spacing.sm,
      paddingTop: spacing.sm,
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: colors.border,
    },
    filterSectionLabel: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '700', marginBottom: spacing.xs },
    filterChipsWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
    filterChip: {
      paddingHorizontal: spacing.sm,
      paddingVertical: 6,
      borderRadius: radius.full,
      backgroundColor: colors.surfaceAlt,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: colors.border,
    },
    filterChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
    filterChipText: { color: colors.textMuted, fontSize: fontSize.sm, fontWeight: '600' },
    filterChipTextActive: { color: '#fff' },
    fab: {
      position: 'absolute',
      right: spacing.lg,
      bottom: spacing.lg,
      width: 52,
      height: 52,
      borderRadius: 26,
      backgroundColor: colors.surface,
      borderWidth: 1.5,
      borderColor: colors.primary,
      alignItems: 'center',
      justifyContent: 'center',
      ...shadow.card,
    },
    fabActive: { backgroundColor: colors.primary },
    fabDot: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: '#fff',
    },
    showResultsPillInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 12,
      paddingHorizontal: spacing.lg,
      borderRadius: radius.full,
      backgroundColor: colors.primary,
      ...shadow.card,
    },
    showResultsPillText: {
      color: '#fff',
      fontSize: fontSize.sm,
      fontWeight: '800',
    },
    feedEmpty: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      gap: spacing.md,
      padding: spacing.xl,
      paddingBottom: 90,
    },
    feedEmptyText: {
      color: colors.textMuted,
      fontSize: fontSize.md,
      textAlign: 'center',
      lineHeight: 22,
    },
    requestsSubtitle: { color: colors.textMuted, fontSize: fontSize.sm, paddingHorizontal: spacing.lg, marginBottom: spacing.sm },
    demandNotice: {
      flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
      marginHorizontal: spacing.lg, marginBottom: spacing.sm,
      backgroundColor: `${colors.primary}18`, borderRadius: radius.md,
      paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    },
    demandNoticeText: { flex: 1, color: colors.primary, fontSize: fontSize.sm, fontWeight: '700' },
    requestsList: { paddingHorizontal: spacing.lg, gap: spacing.sm },
    requestCard: {
      backgroundColor: colors.surface, borderRadius: radius.lg, borderWidth: 1,
      borderColor: colors.border, padding: spacing.md, gap: 6, ...shadow.card,
      position: 'relative',
    },
    requestNewDot: {
      position: 'absolute', top: spacing.md, right: spacing.md,
      width: 8, height: 8, borderRadius: 4, backgroundColor: colors.primary,
    },
    requesterRow: { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 2 },
    requesterName: { flex: 1, color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '600' },
    requesterReliability: { fontSize: fontSize.xs, fontWeight: '800' },
    requestCardTop: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
    requestCardTitle: { flex: 1, color: colors.text, fontSize: fontSize.md, fontWeight: '700' },
    requestCardDesc: { color: colors.textMuted, fontSize: fontSize.sm, lineHeight: 19 },
    statusBadge: { backgroundColor: colors.surfaceAlt, borderRadius: radius.full, paddingHorizontal: 8, paddingVertical: 3 },
    statusBadgeText: { color: colors.textMuted, fontSize: 11, fontWeight: '700' },
    categoryChip: {
      alignSelf: 'flex-start', backgroundColor: colors.surfaceAlt,
      borderRadius: radius.full, paddingVertical: 3, paddingHorizontal: 10,
    },
    categoryChipText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '600' },
    cardChipsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
    ownChip: {
      alignSelf: 'flex-start', backgroundColor: `${colors.primary}22`,
      borderRadius: radius.full, paddingVertical: 3, paddingHorizontal: 10,
    },
    ownChipText: { color: colors.primary, fontSize: fontSize.xs, fontWeight: '700' },
    remoteChip: {
      alignSelf: 'flex-start', backgroundColor: `${colors.textMuted}22`,
      borderRadius: radius.full, paddingVertical: 3, paddingHorizontal: 10,
    },
    remoteChipText: { color: colors.textMuted, fontSize: fontSize.xs, fontWeight: '700' },
  });
}
